import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FaWifi, FaSync, FaCloud, FaExclamationTriangle } from 'react-icons/fa';
import { useOffline } from '../context/OfflineContext';
import { useAuth } from '../contexts/AuthContext';

const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Container = styled.div`
  position: fixed;
  bottom: 20px;
  left: 20px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 768px) {
    bottom: 80px;
    left: 10px;
  }
`;

const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.$offline && css`
    background: #ff5722;
    color: white;
    animation: ${pulse} 2s infinite;
  `}

  ${props => props.$syncing && `
    background: #2196F3;
    color: white;
  `}

  ${props => props.$pending && !props.$offline && !props.$syncing && `
    background: #ff9800;
    color: white;
  `}

  ${props => !props.$offline && !props.$syncing && !props.$pending && `
    background: #4CAF50;
    color: white;
    opacity: 0.8;
  `}

  &:hover {
    transform: scale(1.05);
    opacity: 1;
  }

  svg {
    font-size: 1rem;
  }

  .spin {
    animation: ${spin} 1s linear infinite;
  }
`;

const PendingInfo = styled.div`
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.75rem;
  max-width: 200px;

  .title {
    font-weight: 600;
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .item {
    display: flex;
    justify-content: space-between;
    margin: 4px 0;
    color: #ccc;
  }

  .count {
    color: #ff9800;
    font-weight: 600;
  }

  .sync-btn {
    margin-top: 8px;
    padding: 6px 12px;
    background: #4CAF50;
    border: none;
    border-radius: 4px;
    color: white;
    font-size: 0.75rem;
    cursor: pointer;
    width: 100%;

    &:hover {
      background: #43a047;
    }

    &:disabled {
      background: #666;
      cursor: not-allowed;
    }
  }
`;

const OfflineIndicator = () => {
  const { isOnline, isSyncing, syncStatus, syncPendingData } = useOffline();
  const { isAdmin } = useAuth();
  const [showDetails, setShowDetails] = React.useState(false);

  const { pendingOrders, pendingClients, hasPendingData } = syncStatus;

  // Only show to admin users
  if (!isAdmin) {
    return null;
  }

  // Don't show if online with no pending data and not syncing
  if (isOnline && !hasPendingData && !isSyncing) {
    return null;
  }

  const handleSync = async () => {
    if (isOnline && !isSyncing) {
      await syncPendingData();
    }
  };

  return (
    <Container>
      {showDetails && hasPendingData && (
        <PendingInfo>
          <div className="title">
            <FaExclamationTriangle />
            Pendiente de sincronizar
          </div>
          {pendingOrders > 0 && (
            <div className="item">
              <span>Pedidos:</span>
              <span className="count">{pendingOrders}</span>
            </div>
          )}
          {pendingClients > 0 && (
            <div className="item">
              <span>Clientes:</span>
              <span className="count">{pendingClients}</span>
            </div>
          )}
          {isOnline && (
            <button
              className="sync-btn"
              onClick={handleSync}
              disabled={isSyncing}
            >
              {isSyncing ? 'Sincronizando...' : 'Sincronizar ahora'}
            </button>
          )}
        </PendingInfo>
      )}

      <StatusBadge
        $offline={!isOnline}
        $syncing={isSyncing}
        $pending={hasPendingData}
        onClick={() => setShowDetails(!showDetails)}
      >
        {!isOnline ? (
          <>
            <FaWifi />
            <span>Sin conexion</span>
          </>
        ) : isSyncing ? (
          <>
            <FaSync className="spin" />
            <span>Sincronizando...</span>
          </>
        ) : hasPendingData ? (
          <>
            <FaCloud />
            <span>{pendingOrders + pendingClients} pendientes</span>
          </>
        ) : (
          <>
            <FaWifi />
            <span>Conectado</span>
          </>
        )}
      </StatusBadge>
    </Container>
  );
};

export default OfflineIndicator;
